import React, { useState } from "react";
import { FormData, FormField } from "../types";
import FormInput from "./FormInput";
import { Mail, Loader2 } from "lucide-react";
import { Button } from "components/ui/button";
import axiosInstance from 'api/axios';
import toast from "react-hot-toast";

const ContactForm = () => {
  const [formData, setFormData] = useState<FormData>({
    firstName: "",
    lastName: "",
    email: "",
    teleUsername: "",
    message: "",
  });

  const [isLoading, setIsLoading] = useState(false);

  const formFields: FormField[] = [
    {
      id: "firstName",
      name: "firstName",
      label: "First Name",
      type: "text",
      placeholder: "John",
      required: true,
    },
    {
      id: "lastName",
      name: "lastName",
      label: "Last Name",
      type: "text",
      placeholder: "Smith",
      required: true,
    },
    {
      id: "email",
      name: "email",
      label: "Email (Required if no Telegram username)",
      type: "email",
      placeholder: "johnsmith@email.com",
      required: false,
    },
    {
      id: "teleUsername",
      name: "teleUsername",
      label: "Telegram Username (Required if no email)",
      type: "text",
      placeholder: "@username",
      required: false,
    },
    {
      id: "message",
      name: "message",
      label: "Message",
      type: "textarea",
      placeholder: "Write the message you want to send us",
      required: true,
      rows: 4,
    },
  ];

  const validateForm = () => {
    if (!formData.email && !formData.teleUsername) {
      toast.error("Please provide either an email address or Telegram username");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const response = await axiosInstance.public.post(
        "api/v1/contact-us/submit-contact-form",
        {
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email || undefined,
          tele_username: formData.teleUsername || undefined,
          message: formData.message,
        },
        {
          headers: {
            "Content-Type": "application/json",
            accept: "application/json",
          },
        }
      );

      if (!response.data.success) {
        throw new Error(response.data.message || "Something went wrong");
      }

      toast.success("Message sent successfully! We'll get back to you soon.");
      
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        teleUsername: "",
        message: "",
      });
    } catch (err) {
      toast.error("Failed to submit form");
      
      console.error("Contact form submission error:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
        {formFields.slice(0, 2).map((field) => (
          <FormInput
            key={field.id}
            field={field}
            value={formData[field.name as keyof FormData]}
            onChange={handleChange}
          />
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
        {formFields.slice(2, 4).map((field) => (
          <FormInput
            key={field.id}
            field={field}
            value={formData[field.name as keyof FormData]}
            onChange={handleChange}
          />
        ))}
      </div>

      <FormInput
        field={formFields[4]}
        value={formData.message}
        onChange={handleChange}
      />

      <Button
        variant="customPrimary"
        type="submit"
        disabled={isLoading}
        className="w-full flex items-center justify-center gap-2 px-4 sm:px-6 py-2 sm:py-6"
      >
        {isLoading ? (
          <Loader2 className="h-5 w-5 sm:h-6 sm:w-6 animate-spin" />
        ) : (
          <Mail className="h-5 w-5 sm:h-6 sm:w-6" />
        )}
        <span className="text-base sm:text-lg font-medium">
          {isLoading ? "Sending..." : "Contact Us"}
        </span>
      </Button>
    </form>
  );
};

export default ContactForm;
